const { REACT_APP_DOMAIN } = process.env;

/**
 * Takes in a list of FHIR Patient objects and returns a list of patient objects.
 * @param {Array<Object>} data - the list of patient data.
 * @returns {Array<Patient>} - the list of patient objects.
 */
export function fPatients(data) {
  const patientArray = [];
  data.forEach((element) => {
    const patientObj = fPatient(element);
    patientArray.push(patientObj);
  });

  return patientArray;
}

/**
 * It takes a FHIR Patient resource and returns a JavaScript object with the same data.
 * @param {object} element - the element to convert to a patient object.
 * @returns {object} - the patient object.
 */
export function fPatient(element) {
  let patientObj = {
    id: element.id,
    name_family: '',
    name_given: '',
    name_suffix: '',
    gender: element.gender ? element.gender : '',
    birthDate: element.birthDate ? element.birthDate : '',
    maritalStatus_code: '',
    address_line: '',
    address_city: '',
    address_postalCode: '',
    address_country: '',
    photo_url: '',
    deceasedBoolean: false,
    telecom_phone_value: '',
    telecom_email_value: '',
    contact_name_text: '',
    contact_relationship_text: '',
    contact_telecom_value: '',
    'philhealthNo-id': '',
    'SSSNo-id': '',
    'GSISNo-id': '',
    'patient-pregnancyStatus': false,
    'patient-birthPlace': '',
    'patient-hmo': '',
    'patient-mothersMaidenName': '',
    'patient-nationality': '',
    'patient-remarks': '',
    'patient-lifeline-config-ecg_st_msec': '',
    'patient-lifeline-config-heartrate_upper_bpm': '',
    'patient-lifeline-config-heartrate_lower_bpm': '',
    'patient-lifeline-config-pulserate_upper_bpm': '',
    'patient-lifeline-config-pulserate_lower_bpm': '',
    'patient-lifeline-config-oxygen_upper_saturation': '',
    'patient-lifeline-config-oxygen_lower_saturation': '',
    'patient-lifeline-config-respiratory_upper_rpm': '',
    'patient-lifeline-config-respiratory_lower_rpm': '',
    'patient-lifeline-config-bp_systolic_upper': '',
    'patient-lifeline-config-bp_systolic_lower': '',
    'patient-lifeline-config-bp_diastolic_upper': '',
    'patient-lifeline-config-bp_diastolic_lower': '',
    'patient-lifeline-config-time_frame': '',
    'patient-lifeline-config-temperature_upper': '',
    'patient-lifeline-config-temperature_lower': '',
  };

  if (Array.isArray(element.name)) {
    patientObj = {
      ...patientObj,
      name_family: element.name[0].family ? element.name[0].family : '',
      name_given: element.name[0].given ? element.name[0].given.join(' ') : '',
      name_suffix: element.name[0].suffix ? element.name[0].suffix : '',
    };
  }

  if (element.maritalStatus) {
    if (Array.isArray(element.maritalStatus.coding)) {
      patientObj = {
        ...patientObj,
        maritalStatus_code: element.maritalStatus.coding[0].code || '',
      };
    }
  }

  if (Array.isArray(element.address)) {
    patientObj = {
      ...patientObj,
      address_line: element.address[0].line ? element.address[0].line : '',
      address_city: element.address[0].city ? element.address[0].city : '',
      address_postalCode: element.address[0].postalCode ? element.address[0].postalCode : '',
      address_country: element.address[0].country ? element.address[0].country : '',
    };
  }

  if (Array.isArray(element.address)) {
    patientObj = {
      ...patientObj,
      address_line: element.address[0].line ? element.address[0].line : '',
      address_city: element.address[0].city ? element.address[0].city : '',
      address_postalCode: element.address[0].postalCode ? element.address[0].postalCode : '',
      address_country: element.address[0].country ? element.address[0].country : '',
    };
  }

  if (Array.isArray(element.telecom)) {
    const phone = element.telecom.filter((el) => el.system === 'phone');
    const email = element.telecom.filter((el) => el.system === 'email');

    if (phone.length > 0) {
      patientObj = {
        ...patientObj,
        telecom_phone_value: phone[0].value,
      };
    }

    if (email.length > 0) {
      patientObj = {
        ...patientObj,
        telecom_email_value: email[0].value,
      };
    }
  }

  if (Array.isArray(element.contact)) {
    patientObj = {
      ...patientObj,
      contact_name_text: element.contact[0].name ? element.contact[0].name.text : '',
      contact_relationship_text: element.contact[0].relationship ? element.contact[0].relationship[0].text : '',
    };
    if (Array.isArray(element.contact[0].telecom)) {
      patientObj = {
        ...patientObj,
        contact_telecom_value: element.contact[0].telecom[0] ? element.contact[0].telecom[0].value : '',
      };
    }
  }

  if (Array.isArray(element.identifier)) {
    element.identifier.forEach((identifier) => {
      if (identifier.system === `${REACT_APP_DOMAIN}/NamingSystem/philhealthNo-id`) {
        patientObj = {
          ...patientObj,
          'philhealthNo-id': identifier.value,
        };
      }

      if (identifier.system === `${REACT_APP_DOMAIN}/NamingSystem/SSSNo-id`) {
        patientObj = {
          ...patientObj,
          'SSSNo-id': identifier.value,
        };
      }

      if (identifier.system === `${REACT_APP_DOMAIN}/NamingSystem/GSISNo-id`) {
        patientObj = {
          ...patientObj,
          'GSISNo-id': identifier.value,
        };
      }
    });
  }

  if (Array.isArray(element.extension)) {
    const lifelineConfig = [
      'patient-lifeline-config-ecg_st_msec',
      'patient-lifeline-config-heartrate_upper_bpm',
      'patient-lifeline-config-heartrate_lower_bpm',
      'patient-lifeline-config-pulserate_upper_bpm',
      'patient-lifeline-config-pulserate_lower_bpm',
      'patient-lifeline-config-oxygen_upper_saturation',
      'patient-lifeline-config-oxygen_lower_saturation',
      'patient-lifeline-config-respiratory_upper_rpm',
      'patient-lifeline-config-respiratory_lower_rpm',
      'patient-lifeline-config-bp_systolic_upper',
      'patient-lifeline-config-bp_systolic_lower',
      'patient-lifeline-config-bp_diastolic_upper',
      'patient-lifeline-config-bp_diastolic_lower',
      'patient-lifeline-config-time_frame',
      'patient-lifeline-config-temperature_upper',
      'patient-lifeline-config-temperature_lower',
    ];

    element.extension.forEach((extension) => {
      if (extension.url === 'http://hl7.org/fhir/StructureDefinition/patient-mothersMaidenName') {
        patientObj = {
          ...patientObj,
          'patient-mothersMaidenName': extension.valueString,
        };
      }

      if (extension.url === 'http://hl7.org/fhir/StructureDefinition/patient-birthPlace') {
        patientObj = {
          ...patientObj,
          'patient-birthPlace': extension.valueAddress.text,
        };
      }

      if (extension.url === 'http://hl7.org/fhir/StructureDefinition/patient-nationality') {
        patientObj = {
          ...patientObj,
          'patient-nationality': extension.valueString,
        };
      }

      if (extension.url === `${REACT_APP_DOMAIN}/StructureDefinition/patient-hmo`) {
        patientObj = {
          ...patientObj,
          'patient-hmo': extension.valueString,
        };
      }

      if (extension.url === `${REACT_APP_DOMAIN}/StructureDefinition/patient-pregnancyStatus`) {
        patientObj = {
          ...patientObj,
          'patient-pregnancyStatus': extension.valueBoolean,
        };
      }

      if (extension.url === `${REACT_APP_DOMAIN}/StructureDefinition/patient-remarks`) {
        patientObj = {
          ...patientObj,
          'patient-remarks': extension.valueString,
        };
      }

      if (extension.url === `${REACT_APP_DOMAIN}/StructureDefinition/patient-lifeline-config`) {
        extension.extension.forEach((ext) => {
          if (lifelineConfig.includes(`patient-lifeline-config-${ext.url}`)) {
            patientObj[`patient-lifeline-config-${ext.url}`] = ext.valueString;
          }
        });
      }
    });
  }

  return patientObj;
}
